// WP0 Task 5 — pricing DISPLAY content generated from PLANS/TRACKING_PLAN
// (spec §6: "/pricing — ONE comparison table, figures generated from PLANS").
// Kills the 4th hand-synced copy (inventory 04 §3: marketing content.ts TIERS/
// ADDONS restate constants.ts by hand).
//
// Leaf charter: no runtime imports (type-only from constants). Callers inject
// the real constants — `buildTierDisplay(PLANS, TRACKING_PLAN)` — so figures
// CANNOT be hand-written at a call site, while node --test keeps working.
// TRACKING.credits / TRACKING.monthly are deliberately NOT accepted here:
// internal metering never surfaces (locked #11).
import type { PlanKey } from './constants.js';

export type PublicPlanKey = Exclude<PlanKey, 'starter'>;

/** The exactly-three sold tiers, in display order. The retired `starter` key
 *  (old $19 plan) must never appear on a pricing surface (constants.ts trap). */
export const PUBLIC_PLAN_ORDER = ['pro', 'business', 'growth'] as const satisfies readonly PublicPlanKey[];

export function formatUsd(cents: number): string {
  const dollars = cents / 100;
  return Number.isInteger(dollars)
    ? `$${dollars.toLocaleString('en-US')}`
    : `$${dollars.toFixed(2)}`;
}

/** Structural slice of a PLANS entry this module reads. */
export interface PlanPricingSource {
  label: string;
  monthlyPriceCents: number;
  annualPriceCents: number;
  pageLimit: number;
  aiGenerationsMonthly: number;
  freeMailboxesIncluded: number;
  freeDomainsIncluded: number;
  locationsIncluded: number;
  whiteLabel: boolean;
}

/** Structural slice of a TRACKING_PLAN entry (plain units — the product). */
export interface PlanUnitsSource {
  searchTerms: number;
  competitors: number;
  aiQuestions: number;
  gridOption: string;
  cadence: string;
}

export interface TierDisplay {
  key: PublicPlanKey;
  /** ONLY from PLANS[key].label — the pro-sold-as-"Starter" mapping (locked #20). */
  name: string;
  monthlyUsd: number;
  annualUsd: number;
  monthly: string;
  annual: string;
  pageLimit: number;
  aiHelpsMonthly: number;
  mailboxesIncluded: number;
  freeDomainsIncluded: number;
  locationsIncluded: number;
  whiteLabel: boolean;
  searchTerms: number;
  competitors: number;
  aiQuestions: number;
  gridOption: string;
  cadence: string;
}

export function buildTierDisplay(
  plans: Record<PublicPlanKey, PlanPricingSource>,
  units: Record<PublicPlanKey, PlanUnitsSource>,
): TierDisplay[] {
  return PUBLIC_PLAN_ORDER.map((key) => {
    const p = plans[key];
    const u = units[key];
    return {
      key,
      name: p.label,
      monthlyUsd: p.monthlyPriceCents / 100,
      annualUsd: p.annualPriceCents / 100,
      monthly: formatUsd(p.monthlyPriceCents),
      annual: formatUsd(p.annualPriceCents),
      pageLimit: p.pageLimit,
      aiHelpsMonthly: p.aiGenerationsMonthly,
      mailboxesIncluded: p.freeMailboxesIncluded,
      freeDomainsIncluded: p.freeDomainsIncluded,
      locationsIncluded: p.locationsIncluded,
      whiteLabel: p.whiteLabel,
      searchTerms: u.searchTerms,
      competitors: u.competitors,
      aiQuestions: u.aiQuestions,
      gridOption: u.gridOption,
      cadence: u.cadence,
    };
  });
}

export interface AddonDisplay {
  id: 'ai_pack' | 'mailbox' | 'search_terms' | 'location';
  label: string;
  price: string;
  unit: string;
}

export function buildAddonDisplay(src: {
  aiPack: { generations: number; priceCents: number };
  mailbox: { priceCents: number };
  searchTerms: { termsPerPack: number; priceCents: number; maxPacks: number };
  location: { priceCents: number; extraSearchTerms: number; extraCompetitors: number };
}): AddonDisplay[] {
  return [
    {
      id: 'ai_pack',
      label: 'extra AI helps',
      price: `${formatUsd(src.aiPack.priceCents)} / ${src.aiPack.generations}-pack`,
      unit: `${src.aiPack.generations} more AI helps, whenever your plan runs low`,
    },
    {
      id: 'mailbox',
      label: 'workspace mailbox',
      price: `+${formatUsd(src.mailbox.priceCents)}/mo`,
      unit: 'a professional inbox on your own domain',
    },
    {
      // 'search terms' literal is lockstep with glossary TERMS.searchTerms
      // (leaf rule — asserted in faq/pricing tests via glossary import).
      id: 'search_terms',
      label: 'more tracked search terms',
      price: `+${formatUsd(src.searchTerms.priceCents)}/mo per pack of ${src.searchTerms.termsPerPack}`,
      unit: `up to ${src.searchTerms.maxPacks} packs`,
    },
    {
      id: 'location',
      label: 'extra location',
      price: `${formatUsd(src.location.priceCents)}/mo each`,
      unit: `each brings ${src.location.extraSearchTerms} search terms and ${src.location.extraCompetitors} competitors of its own`,
    },
  ];
}
